import { useState, useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';

import { Auth0Provider } from '@auth0/auth0-react';
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';

import { getConfig } from "./config";
import { getLocal, getTenant, setLocal } from './utils/api';

import App from './App';

const createMsalInstance = async (tenant) => {
  const msalInstance = new PublicClientApplication({
    auth: {
      clientId: tenant.clientId,
      authority: `https://${process.env.REACT_APP_ENTRA_SUBDOMAIN}.ciamlogin.com/${process.env.REACT_APP_ENTRA_TENANT_ID}`,
      redirectUri: window.location.origin,
      postLogoutRedirectUri: window.location.origin,
      knownAuthorities: [`${process.env.REACT_APP_ENTRA_SUBDOMAIN}.ciamlogin.com`]
    },
    cache: {
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: true
    }
  });

  await msalInstance.initialize();

  try {
    const redirectResponse = await msalInstance.handleRedirectPromise();

    if (redirectResponse) {
      setLocal('login', true);
      setLocal('token', redirectResponse.accessToken);
    }
  } catch (e) {
    console.error('MSAL redirect error:', e);
  }

  const accounts = msalInstance.getAllAccounts();

  if (accounts.length > 0 && !msalInstance.getActiveAccount()) {
    msalInstance.setActiveAccount(accounts[0]);
  }

  return msalInstance;
};

function AuthGate() {
  const [authState, setAuthState] = useState({
    ready: false,
    type: null,
    msalInstance: null,
    auth0Config: null,
    tenant: null
  });

  useEffect(() => {
    const init = async () => {
      const cached = getLocal('tenant');

      if (cached) {
        await resolveAuth(cached);
        return;
      }

      const locationParts = window.location.pathname.split('/');
      const slug = (locationParts.length === 2 && locationParts[1] !== '' ? locationParts[1] : null);

      if (slug) {
        try {
          const tenant = await getTenant(slug);

          if (tenant) {
            await resolveAuth(tenant);
            return;
          }
        } catch (e) {
          console.error('Tenant fetch error:', e);
        }
      }

      await resolveAuth({ authType: 'auth0' });
    };

    const resolveAuth = async (tenant) => {
      if (tenant.idpType === 'saml') {
        const instance = await createMsalInstance(tenant);

        setAuthState({
          ready: true,
          type: 'saml',
          msalInstance: instance,
          auth0Config: null,
          tenant: tenant
        });
      } else {
        const config = getConfig();

        setAuthState({
          ready: true,
          type: 'auth0',
          msalInstance: null,
          auth0Config: {
            domain: config.domain,
            clientId: config.clientId,
            authorizationParams: {
              redirect_uri: window.location.origin,
              screen_hint: (getLocal('referralCode') || getLocal('signup') ? 'signup' : 'login'),
              acr_values: (getLocal('demo') || process.env.REACT_APP_AUTH0_ENV),
              ...(config.audience ? { audience: config.audience } : null)
            },
            onRedirectCallback: (appState) => {
              setLocal('login', true);

              window.history.replaceState({}, document.title, appState?.returnTo || window.location.pathname);
            }
          },
          tenant: tenant
        });
      }
    };

    init();
  }, []);

  if (!authState.ready) {
    return null;
  }

  if (authState.type === 'saml') {
    return (
      <MsalProvider instance={authState.msalInstance}>
        <BrowserRouter>
          <App authState={authState} />
        </BrowserRouter>
      </MsalProvider>
    );
  }

  return (
    <Auth0Provider {...authState.auth0Config}>
      <BrowserRouter>
        <App authState={authState} />
      </BrowserRouter>
    </Auth0Provider>
  );
}

export default AuthGate;